import { createFileRoute } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { ErrorState, Skeleton, EmptyState } from '@vta/ui';
import type { Program, TeamSummary } from '@vta/types';
import { apiClient, ApiError } from '../lib/api-client.ts';
import { OgMeta } from '../lib/og-meta.tsx';
import { CityHeader } from '../features/cities/CityHeader.tsx';
import { CityTeamList } from '../features/cities/CityTeamList.tsx';

interface CityResponse {
  city: {
    citySlug?: string;
    city?: string | null;
    region?: string | null;
    country?: string | null;
    countrySlug?: string | null;
    lat?: number | null;
    lon?: number | null;
    teamCount?: number;
    programBreakdown?: Partial<Record<Program, number>> | null;
  };
  teams: TeamSummary[];
}

export const Route = createFileRoute('/city/$citySlug')({
  loader: async ({ params }) => {
    try {
      return await apiClient.get<CityResponse>(`/api/cities/${encodeURIComponent(params.citySlug)}`);
    } catch (err) {
      if (err instanceof ApiError && err.status === 404) return null;
      throw err;
    }
  },
  component: CityRoute,
});

function CityRoute() {
  const { citySlug } = Route.useParams();
  const initial = Route.useLoaderData() as CityResponse | null;

  const query = useQuery<CityResponse | null>({
    queryKey: ['city', citySlug],
    initialData: initial,
    queryFn: ({ signal }) =>
      apiClient
        .get<CityResponse>(`/api/cities/${encodeURIComponent(citySlug)}`, { signal })
        .catch((err) => {
          if (err instanceof ApiError && err.status === 404) return null;
          throw err;
        }),
  });

  if (query.isLoading) {
    return <CitySkeleton />;
  }

  if (query.isError) {
    return (
      <main className="container mx-auto py-10">
        <ErrorState
          title="Could not load this city"
          retry={() => query.refetch()}
        />
      </main>
    );
  }

  const data = query.data;

  if (!data) {
    return (
      <main className="container mx-auto py-10">
        <EmptyState
          title="City not found"
          description="The slug may be wrong or the city has no catalogued teams yet."
        />
      </main>
    );
  }

  const cluster = {
    city: data.city.city ?? null,
    region: data.city.region ?? null,
    country: data.city.country ?? null,
    countrySlug: data.city.countrySlug ?? null,
    teamCount: data.city.teamCount ?? data.teams.length,
    programBreakdown: data.city.programBreakdown ?? deriveBreakdown(data.teams),
  };

  const title = cluster.city ? `${cluster.city}` : citySlug;
  const description = cluster.country
    ? `${cluster.teamCount} VEX teams catalogued in ${cluster.city ?? citySlug}, ${cluster.country}.`
    : `${cluster.teamCount} VEX teams catalogued in ${cluster.city ?? citySlug}.`;

  return (
    <>
      <OgMeta title={title} description={description} pathname={`/city/${citySlug}`} />
      <main className="container mx-auto flex flex-col gap-10 py-8 md:py-12">
        <CityHeader cluster={cluster} />

        <div className="grid gap-10 lg:grid-cols-[2fr_1fr]">
          <Section title="Teams" subtitle={`${cluster.teamCount} on file`}>
            <CityTeamList teams={data.teams} />
          </Section>

          <aside className="flex flex-col gap-3">
            <header className="flex items-baseline justify-between gap-3 border-b border-border-subtle pb-2">
              <h2 className="font-display text-xl text-fg">Globe preview</h2>
              <p className="text-2xs uppercase tracking-[0.18em] text-fg-subtle">Centered on city</p>
            </header>
            <GlobePreviewFallback
              lat={data.city.lat ?? null}
              lon={data.city.lon ?? null}
            />
          </aside>
        </div>
      </main>
    </>
  );
}

function deriveBreakdown(teams: TeamSummary[]): Partial<Record<Program, number>> {
  const out: Partial<Record<Program, number>> = {};
  for (const t of teams) out[t.program] = (out[t.program] ?? 0) + 1;
  return out;
}

function Section({
  title,
  subtitle,
  children,
}: {
  title: string;
  subtitle?: string;
  children: React.ReactNode;
}) {
  return (
    <section className="flex flex-col gap-3">
      <header className="flex items-baseline justify-between gap-3 border-b border-border-subtle pb-2">
        <h2 className="font-display text-xl text-fg">{title}</h2>
        {subtitle ? (
          <p className="text-2xs uppercase tracking-[0.18em] text-fg-subtle">{subtitle}</p>
        ) : null}
      </header>
      {children}
    </section>
  );
}

/**
 * GlobePreview fallback. The real component lives in features/globe/ which is
 * owned by the Web Shell agent. Until that ships we render a Skeleton plus
 * a friendly "coming online" message — same shape, same height, so layout
 * doesn't shift when it lands.
 */
function GlobePreviewFallback({ lat, lon }: { lat: number | null; lon: number | null }) {
  return (
    <div className="relative aspect-square w-full overflow-hidden rounded-lg border border-border-subtle bg-bg-raised">
      <Skeleton className="absolute inset-0 rounded-none" />
      <div className="absolute inset-0 flex flex-col items-center justify-center gap-1 px-4 text-center">
        <p className="text-2xs uppercase tracking-[0.22em] text-fg-subtle">Live preview coming online</p>
        {lat !== null && lon !== null ? (
          <p className="font-mono text-xs text-fg-muted">
            {lat.toFixed(2)}°, {lon.toFixed(2)}°
          </p>
        ) : null}
      </div>
    </div>
  );
}

function CitySkeleton() {
  return (
    <main className="container mx-auto flex flex-col gap-6 py-10">
      <Skeleton className="h-12 w-2/3" />
      <Skeleton className="h-5 w-1/2" />
      <div className="mt-4 grid gap-6 lg:grid-cols-[2fr_1fr]">
        <Skeleton className="h-72 w-full" />
        <Skeleton className="aspect-square w-full" />
      </div>
    </main>
  );
}
